import { useState, useEffect, useCallback } from "react";
import { Card, DatePicker, Spin, message, Table, Tag, Row, Col, Typography, Space } from "antd";
import { UserOutlined } from "@ant-design/icons";
import withRoleAccess from "../../hoc/withRoleAccess";
import axios from "axios";
import dayjs from "dayjs";
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrBefore);

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

/* ── Design tokens ─────────────────────────────────── */
const PRIMARY    = "#485daa";
const PRIMARY_DK = "#3a4d96";
const PRIMARY_LT = "#eef0f9";
const SUCCESS    = "#16a34a";
const SUCCESS_LT = "#f0fdf4";
const DANGER     = "#dc2626";
const BORDER     = "#e5e7eb";
const MUTED      = "#9ca3af";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700&family=DM+Sans:wght@300;400;500&display=swap');

  .cd-wrap * { font-family: 'DM Sans', sans-serif; }

  /* ── Header card ─────────────────────────────────── */
  .cd-header { margin-bottom: 20px; border-radius: 12px !important; border: none !important; overflow: hidden; }
  .cd-header .ant-card-body { padding: 20px 24px !important; background: linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DK} 100%); }

  /* ── Info bar ────────────────────────────────────── */
  .cd-infobar { margin-bottom: 20px; border-radius: 12px !important; border: 1.5px solid ${BORDER} !important; }
  .cd-infobar .ant-card-body { padding: 14px 24px !important; }

  /* ── Stat mini cards ─────────────────────────────── */
  .cd-stat { border-radius: 10px; border: 1.5px solid ${BORDER}; background: #fff; padding: 14px 18px; }
  .cd-stat-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: ${MUTED}; margin-bottom: 4px; }
  .cd-stat-value { font-family: 'Sora', sans-serif; font-size: 20px; font-weight: 700; line-height: 1.2; }

  /* ── Salesperson card ────────────────────────────── */
  .cd-sp-card { margin-bottom: 24px; border-radius: 12px !important; border: 1.5px solid ${BORDER} !important; box-shadow: 0 2px 12px rgba(72,93,170,0.07) !important; overflow: hidden; }
  .cd-sp-card .ant-card-head { background: ${PRIMARY} !important; border-radius: 0 !important; padding: 0 20px !important; min-height: 48px !important; border-bottom: none !important; }
  .cd-sp-card .ant-card-head-title { color: #fff !important; font-family: 'Sora', sans-serif !important; font-size: 14px !important; font-weight: 600 !important; padding: 12px 0 !important; }
  .cd-sp-card .ant-card-extra { color: rgba(255,255,255,0.85) !important; font-size: 13px !important; padding: 12px 0 !important; }
  .cd-sp-card .ant-card-body { padding: 0 !important; }

  /* ── Main table ──────────────────────────────────── */
  .cd-table .ant-table { font-family: 'DM Sans', sans-serif !important; font-size: 13px !important; }
  .cd-table .ant-table-thead > tr > th {
    background: #f3f5fb !important;
    color: ${PRIMARY} !important;
    font-family: 'Sora', sans-serif !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    border-bottom: 2px solid ${PRIMARY_LT} !important;
    padding: 10px 14px !important;
  }
  .cd-table .ant-table-thead > tr > th::before { display: none !important; }
  .cd-table .ant-table-tbody > tr > td {
    padding: 9px 14px !important;
    border-bottom: 1px solid #f0f0f5 !important;
    background: #fff !important;
    transition: background 0.15s !important;
  }
  .cd-table .ant-table-tbody > tr:nth-child(even) > td { background: #f8f9fe !important; }
  .cd-table .ant-table-tbody > tr:hover > td { background: ${PRIMARY_LT} !important; }
  .cd-table .ant-table-tbody > tr:nth-child(even):hover > td { background: ${PRIMARY_LT} !important; }

  /* ── Summary row ─────────────────────────────────── */
  .cd-table .ant-table-summary > tr > td {
    background: ${PRIMARY_LT} !important;
    border-top: 2px solid ${PRIMARY} !important;
    padding: 11px 14px !important;
  }

  /* ── Expand panel ────────────────────────────────── */
  .cd-table .ant-table-expanded-row > td { background: #f9fafb !important; padding: 0 !important; }
  .cd-expand-wrap { padding: 14px 18px; border-bottom: 1px solid ${BORDER}; }
  .cd-expand-table .ant-table-thead > tr > th {
    background: #f3f4f6 !important; color: #374151 !important;
    font-size: 11px !important; font-weight: 600 !important;
    padding: 7px 10px !important; border-bottom: 1px solid ${BORDER} !important;
  }
  .cd-expand-table .ant-table-thead > tr > th::before { display: none !important; }
  .cd-expand-table .ant-table-tbody > tr > td { padding: 7px 10px !important; font-size: 12px !important; }
`;

const thb = (val, dec = 0) =>
  `฿${Number(val).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;

const CommissionDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([dayjs("2025-01-01"), dayjs("2025-12-31")]);
  const [commissionData, setCommissionData] = useState([]);
  const [commissionTiers, setCommissionTiers] = useState([]);

  const API_URL = process.env.REACT_APP_API_URL;

  const fetchCommissionReport = useCallback(async (startDate, endDate) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const start = dayjs(startDate);
      const end = dayjs(endDate);
      const monthlyReports = [];
      let current = start.startOf('month');

      while (current.isSameOrBefore(end, 'month')) {
        const month = current.month() + 1;
        const year = current.year();
        try {
          const response = await axios.get(`${API_URL}/commission/monthly-report`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { month, year }
          });
          monthlyReports.push({ month, year, monthName: current.format('MMM'), data: response.data || [] });
        } catch (error) {
          console.warn(`No data for ${year}-${month}:`, error);
          monthlyReports.push({ month, year, monthName: current.format('MMM'), data: [] });
        }
        current = current.add(1, 'month');
      }

      const salespersonMap = {};
      monthlyReports.forEach((monthReport) => {
        monthReport.data.forEach(sp => {
          const spId = sp.salesperson.id || sp.salesperson.name;
          if (!salespersonMap[spId]) {
            salespersonMap[spId] = { salesperson: sp.salesperson, monthlyData: {}, totalSales: 0, totalCommission: 0 };
          }
          const salesAfterVAT = sp.totalSales * 0.93;
          const overTarget = sp.commission?.over_target || 0;
          const commission = sp.commission?.total_commission || 0;

          salespersonMap[spId].monthlyData[monthReport.monthName] = {
            sales: sp.totalSales,
            salesAfterVAT,
            overTarget,
            commission,
            breakdown: sp.commission?.breakdown || [],
            groups: sp.groups || [],
            totalGroups: sp.totalGroups || 0
          };
          salespersonMap[spId].totalSales += sp.totalSales;
          salespersonMap[spId].totalCommission += commission;
        });
      });

      setCommissionData(Object.values(salespersonMap));
    } catch (error) {
      console.error("Error fetching commission report:", error);
      message.error("Failed to fetch commission report");
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  const fetchCommissionTiers = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/commission/tiers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCommissionTiers(response.data);
    } catch (error) {
      console.error("Error fetching commission tiers:", error);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchCommissionReport(dateRange[0].format("YYYY-MM-DD"), dateRange[1].format("YYYY-MM-DD"));
    fetchCommissionTiers();
  }, [dateRange, fetchCommissionReport, fetchCommissionTiers]);

  const columns = [
    {
      title: 'Month', dataIndex: 'month', key: 'month', width: 70, fixed: 'left',
      render: (v) => <Text strong style={{ fontFamily: "'Sora',sans-serif", color: PRIMARY }}>{v}</Text>,
    },
    {
      title: 'Target', key: 'target', width: 110,
      render: () => <span style={{ color: MUTED }}>฿600,000</span>,
    },
    {
      title: 'Base Commission 2.5%', key: 'targetCommission', width: 140,
      render: (_, r) => {
        const hit = (r.salesAfterVAT || 0) >= 600000;
        return hit
          ? <span style={{ color: SUCCESS, fontWeight: 600 }}>{thb(600000 * 0.025, 2)}</span>
          : <span style={{ color: MUTED }}>฿0</span>;
      },
    },
    {
      title: 'Sales', dataIndex: 'sales', key: 'sales', width: 130,
      render: (v) => v > 0 ? <Text strong>{thb(v)}</Text> : <span style={{ color: MUTED }}>—</span>,
    },
    {
      title: 'After VAT (−7%)', dataIndex: 'salesAfterVAT', key: 'salesAfterVAT', width: 140,
      render: (v, r) => r.sales > 0 ? thb(v) : <span style={{ color: MUTED }}>—</span>,
    },
    {
      title: 'Over Target', dataIndex: 'overTarget', key: 'overTarget', width: 130,
      render: (v) => v > 0
        ? <Text strong style={{ color: DANGER }}>{thb(v)}</Text>
        : <span style={{ color: MUTED }}>—</span>,
    },
    {
      title: 'Rate', key: 'commissionRate', width: 90,
      render: (_, r) => {
        if (!r.breakdown?.length) return <span style={{ color: MUTED }}>—</span>;
        const item = r.breakdown.find(b =>
          b.tier_range !== "Base Target (600K)" && b.tier_range !== "Below Target (600K)"
        );
        return item
          ? <span style={{ padding: '2px 8px', borderRadius: 12, background: '#fff7e6', color: '#d97706', fontWeight: 700, fontSize: 12 }}>{item.rate}</span>
          : <span style={{ color: MUTED }}>—</span>;
      },
    },
    {
      title: 'Commission', dataIndex: 'commission', key: 'commission', width: 140,
      render: (v) => v > 0
        ? <Text strong style={{ color: SUCCESS, fontFamily: "'Sora',sans-serif", fontSize: 14 }}>{thb(v, 2)}</Text>
        : <span style={{ color: MUTED }}>—</span>,
    },
  ];

  const expandColumns = [
    {
      title: 'Course Code', dataIndex: 'class_code', key: 'class_code', width: 150,
      render: (v) => <Text strong style={{ color: PRIMARY }}>{v || 'No Code'}</Text>,
    },
    {
      title: 'Price', dataIndex: 'price', key: 'price', width: 110,
      render: (v) => <Text strong>{thb(parseFloat(v))}</Text>,
    },
    {
      title: 'Students', dataIndex: 'students', key: 'students',
      render: (students) => {
        if (!students?.length) return <span style={{ color: MUTED }}>—</span>;
        return (
          <Space size={4} wrap>
            {students.slice(0, 2).map((s, i) => (
              <Tag key={i} style={{ background: PRIMARY_LT, color: PRIMARY, border: `1px solid rgba(72,93,170,0.2)`, borderRadius: 20, fontSize: 11 }}>
                {s.nickname || `${s.firstname} ${s.lastname}`}
              </Tag>
            ))}
            {students.length > 2 && <Tag style={{ borderRadius: 20, fontSize: 11 }}>+{students.length - 2}</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Create Date', dataIndex: 'create_date', key: 'create_date', width: 110,
      render: (v) => <span style={{ color: MUTED, fontSize: 12 }}>{dayjs(v).format('DD/MM/YYYY')}</span>,
    },
  ];

  const renderCard = (sp, index) => {
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const tableData = months.map(month => {
      const md = sp.monthlyData[month] || {};
      return {
        key: month, month,
        sales: md.sales || 0,
        salesAfterVAT: md.salesAfterVAT || 0,
        overTarget: md.overTarget || 0,
        commission: md.commission || 0,
        breakdown: md.breakdown || [],
        groups: md.groups || [],
        totalGroups: md.totalGroups || 0,
      };
    });

    const totalCourses = Object.values(sp.monthlyData).reduce((s, m: any) => s + (m.totalGroups || 0), 0);

    return (
      <Card
        key={index}
        className="cd-sp-card"
        title={
          <Space>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UserOutlined style={{ color: '#fff', fontSize: 13 }} />
            </div>
            <span style={{ color: '#fff' }}>{sp.salesperson.name}</span>
            {sp.salesperson.nickname && (
              <span style={{ padding: '1px 8px', borderRadius: 12, background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: 11 }}>
                {sp.salesperson.nickname}
              </span>
            )}
          </Space>
        }
        extra={
          <Space split={<span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>}>
            <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>{totalCourses as any} courses</span>
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>Total: {thb(sp.totalSales)}</span>
          </Space>
        }
      >
        <Table
          className="cd-table"
          columns={columns as any}
          dataSource={tableData}
          pagination={false}
          bordered={false}
          size="small"
          scroll={{ x: 1000 }}
          expandable={{
            expandedRowRender: (record) => {
              if (!record.groups.length) {
                return (
                  <div className="cd-expand-wrap">
                    <Text style={{ color: MUTED }}>No courses for {record.month}</Text>
                  </div>
                );
              }
              const monthTotal = record.groups.reduce((s, g) => s + parseFloat(g.price || 0), 0);
              return (
                <div className="cd-expand-wrap">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <Text strong style={{ color: PRIMARY }}>Courses — {record.month}</Text>
                    <Text strong>Monthly total: {thb(monthTotal)}</Text>
                  </div>
                  <Table
                    className="cd-expand-table"
                    size="small"
                    pagination={false}
                    columns={expandColumns}
                    dataSource={record.groups}
                    rowKey="groupID"
                    style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${BORDER}` }}
                  />
                </div>
              );
            },
            rowExpandable: (r) => r.groups?.length > 0,
          }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} />
                <Table.Summary.Cell index={1} />
                <Table.Summary.Cell index={2}>
                  <Text strong style={{ fontFamily: "'Sora',sans-serif", color: PRIMARY }}>TOTAL</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  <Text strong style={{ fontFamily: "'Sora',sans-serif" }}>{thb(sp.totalSales)}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4}>
                  <Text strong>{thb(sp.totalSales * 0.93)}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} />
                <Table.Summary.Cell index={6} />
                <Table.Summary.Cell index={7}>
                  <Text strong style={{ color: SUCCESS, fontFamily: "'Sora',sans-serif", fontSize: 15 }}>
                    {thb(sp.totalCommission, 2)}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Card>
    );
  };

  const totalSalesAll      = commissionData.reduce((s, sp) => s + sp.totalSales, 0);
  const totalCommissionAll = commissionData.reduce((s, sp) => s + sp.totalCommission, 0);
  const monthsHit          = commissionData.reduce((n, sp) =>
    n + Object.values(sp.monthlyData).filter((m: any) => (m.salesAfterVAT || 0) >= 600000).length, 0
  );

  return (
    <>
      <style>{styles}</style>
      <div className="cd-wrap">

        {/* Header */}
        <Card className="cd-header" bodyStyle={{ padding: 0 }}>
          <div style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DK} 100%)`, padding: '20px 24px', borderRadius: 12 }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={4} style={{ margin: 0, color: '#fff', fontFamily: "'Sora',sans-serif" }}>
                  Commission Dashboard
                </Title>
                <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
                  Fixed rate · 2.5% when monthly sales (after VAT) ≥ ฿600,000
                </Text>
              </Col>
              <Col>
                <Space>
                  <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>Date range:</Text>
                  <RangePicker
                    value={dateRange as any}
                    onChange={(val) => val && setDateRange(val as any)}
                    allowClear={false}
                    format="YYYY-MM-DD"
                    style={{ minWidth: 240, borderRadius: 8 }}
                  />
                </Space>
              </Col>
            </Row>
          </div>
        </Card>

        {/* Summary stats */}
        <Row gutter={16} style={{ marginBottom: 20 }}>
          {[
            { label: 'Total Sales',            value: thb(totalSalesAll),        color: PRIMARY },
            { label: 'Total Commission Paid',  value: thb(totalCommissionAll, 2), color: SUCCESS },
            { label: 'Months Target Hit',      value: `${monthsHit}`,            color: '#f59e0b', sub: 'months' },
            { label: 'Active Salespersons',    value: `${commissionData.length}`, color: '#7c3aed', sub: 'people' },
          ].map(({ label, value, color, sub }) => (
            <Col span={6} key={label}>
              <div className="cd-stat">
                <div className="cd-stat-label">{label}</div>
                <div className="cd-stat-value" style={{ color }}>
                  {value}
                  {sub && <span style={{ fontSize: 13, fontWeight: 400, color: MUTED, marginLeft: 4 }}>{sub}</span>}
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {/* Commission info bar */}
        <Card className="cd-infobar" bodyStyle={{ padding: '14px 24px' }}>
          <Row align="middle" gutter={32}>
            {[
              { label: 'Monthly Target',   value: '฿600,000', color: PRIMARY },
              { label: 'Commission Rate',  value: '2.5%',     color: SUCCESS },
              { label: 'VAT Deducted',     value: '7%',       color: '#f59e0b' },
            ].map(({ label, value, color }) => (
              <Col key={label}>
                <div style={{ fontSize: 11, color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
                <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 700, color }}>{value}</div>
              </Col>
            ))}
            <Col style={{ color: BORDER, fontSize: 20, padding: '0 8px' }}>|</Col>
            <Col flex="auto">
              {commissionTiers.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <Text style={{ fontSize: 11, color: MUTED, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginRight: 4 }}>
                    Over-target tiers:
                  </Text>
                  {commissionTiers.map((tier, idx) => (
                    <Tag key={idx} style={{ background: SUCCESS_LT, color: SUCCESS, border: `1px solid rgba(22,163,74,0.25)`, borderRadius: 20, fontSize: 11 }}>
                      {thb(parseFloat(tier.min_amount))}–{tier.max_amount ? thb(parseFloat(tier.max_amount)) : '∞'}: {(tier.commission_rate * 100).toFixed(2)}%
                    </Tag>
                  ))}
                </div>
              )}
            </Col>
          </Row>
        </Card>

        {/* Salesperson tables */}
        <Spin spinning={loading}>
          {commissionData.length > 0 ? (
            commissionData.map((sp, i) => renderCard(sp, i))
          ) : (
            <Card style={{ borderRadius: 12, border: `1.5px solid ${BORDER}`, textAlign: 'center', padding: 40 }}>
              <Text style={{ color: MUTED }}>No commission data found for the selected date range.</Text>
            </Card>
          )}
        </Spin>
      </div>
    </>
  );
};

export default withRoleAccess(CommissionDashboard, ["superadmin"]);